import { supabase } from '@/integrations/supabase/client';
import { round2 } from '@/utils/workTimeUtils';
import {
  PayrollDeduction,
  calculateAHVDeduction,
  calculateALVDeduction,
  calculateBVGDeduction,
  SWISS_INSURANCE_RATES
} from './payrollCalculations';
import { getEmployeeOverride } from '@/config/employeeOverrides';

export interface PayrollOverride {
  id: string;
  employee_id: string;
  effective_from: string;
  target_net_amount: number;
  include_insurance_codes: string[];
  exclude_tax: boolean;
  payslip_minimal: boolean;
  active: boolean;
  notes?: string;
}

export interface FixedNetResult {
  gross_salary: number;
  net_salary: number;
  total_deductions: number;
  deductions: PayrollDeduction[];
  is_override: boolean;
  payslip_minimal: boolean;
}

// AXA-based UVG and KTG rates (example rates - adjust as needed)
const AXA_INSURANCE_RATES = {
  UVG_NBU: 0.0017, // 0.17% UVG Non-occupational accidents
  KTG: 0.0083,     // 0.83% Daily benefits (KTG)
};

export function calculateUVGDeduction(grossSalary: number): PayrollDeduction {
  const deductionAmount = grossSalary * AXA_INSURANCE_RATES.UVG_NBU;
  
  return {
    type: 'uvg',
    description: 'UVG-NBU (AXA)',
    rate_percent: AXA_INSURANCE_RATES.UVG_NBU * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateKTGDeduction(grossSalary: number): PayrollDeduction {
  const deductionAmount = grossSalary * AXA_INSURANCE_RATES.KTG;
  
  return {
    type: 'ktg',
    description: 'Krankentaggeld (AXA)',
    rate_percent: AXA_INSURANCE_RATES.KTG * 100,
    base_amount: grossSalary,
    deduction_amount: round2(deductionAmount),
  };
}

export function calculateMandatorySwissDeductions(
  grossSalary: number,
  birthDate?: string,
  includeInsuranceCodes: string[] = ['AHV', 'ALV', 'BVG', 'UVG', 'KTG']
): PayrollDeduction[] {
  const deductions: PayrollDeduction[] = [];
  
  // Calculate annual salary for ALV threshold check
  const annualSalary = grossSalary * 12;
  
  // AHV/IV/EO (mandatory for all)
  if (includeInsuranceCodes.includes('AHV')) {
    deductions.push(calculateAHVDeduction(grossSalary));
  }
  
  // ALV (unemployment insurance)
  if (includeInsuranceCodes.includes('ALV')) {
    deductions.push(calculateALVDeduction(grossSalary, annualSalary));
  }
  
  // BVG (pension fund) - age dependent
  if (includeInsuranceCodes.includes('BVG')) {
    const bvgDeduction = calculateBVGDeduction(grossSalary, birthDate);
    if (bvgDeduction.deduction_amount > 0) {
      deductions.push(bvgDeduction);
    }
  }
  
  // UVG Non-occupational accidents
  if (includeInsuranceCodes.includes('UVG')) {
    deductions.push(calculateUVGDeduction(grossSalary));
  }
  
  // KTG Daily benefits insurance
  if (includeInsuranceCodes.includes('KTG')) {
    deductions.push(calculateKTGDeduction(grossSalary));
  }
  
  return deductions;
}

export function solveGrossForTargetNet(
  targetNet: number,
  birthDate?: string,
  includeInsuranceCodes: string[] = ['AHV', 'ALV', 'BVG', 'UVG', 'KTG'],
  tolerance: number = 0.01,
  maxIterations: number = 50
): number {
  let low = targetNet;
  let high = targetNet * 2; // Start with double the target as upper bound
  
  for (let i = 0; i < maxIterations; i++) {
    const midGross = (low + high) / 2;
    const deductions = calculateMandatorySwissDeductions(midGross, birthDate, includeInsuranceCodes);
    const totalDeductions = deductions.reduce((sum, d) => sum + d.deduction_amount, 0);
    const calculatedNet = midGross - totalDeductions;
    
    if (Math.abs(calculatedNet - targetNet) <= tolerance) {
      return round2(midGross);
    }
    
    if (calculatedNet > targetNet) {
      high = midGross;
    } else {
      low = midGross;
    }
  }
  
  // Fallback: return the mid point if we don't converge
  return round2((low + high) / 2);
}

export async function getEmployeePayrollOverride(
  employeeId: string,
  effectiveDate: Date = new Date()
): Promise<PayrollOverride | null> {
  const effectiveDateStr = effectiveDate.toISOString().split('T')[0];
  
  const { data: overrides, error } = await supabase
    .from('employee_payroll_overrides')
    .select('*')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .lte('effective_from', effectiveDateStr)
    .or(`effective_to.is.null,effective_to.gte.${effectiveDateStr}`)
    .order('effective_from', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Error fetching payroll override:', error);
    return null;
  }

  if (!overrides || overrides.length === 0) {
    return null;
  }

  const override = overrides[0];
  return {
    id: override.id,
    employee_id: override.employee_id,
    effective_from: override.effective_from,
    target_net_amount: override.target_net_amount,
    include_insurance_codes: override.include_insurance_codes,
    exclude_tax: override.exclude_tax,
    payslip_minimal: override.payslip_minimal,
    active: override.active,
    notes: override.notes
  };
}

export async function calculateFixedNetPayroll(
  employeeId: string,
  employeeBirthDate?: string,
  effectiveDate: Date = new Date()
): Promise<FixedNetResult | null> {
  const override = await getEmployeePayrollOverride(employeeId, effectiveDate);

  if (!override) {
    return null;
  }

  // Check for one-time monthly net adjustments from employee overrides
  const { data: emp } = await supabase
    .from('employees')
    .select('email')
    .eq('id', employeeId)
    .single();
  const empOverrides = emp?.email ? getEmployeeOverride(emp.email) : undefined;
  const monthKey = `${effectiveDate.getFullYear()}-${String(effectiveDate.getMonth() + 1).padStart(2, '0')}`;
  const netAdjustment = empOverrides?.monthlyNetAdjustments?.[monthKey] ?? 0;

  const targetNet = override.target_net_amount + netAdjustment;

  const grossSalary = solveGrossForTargetNet(
    targetNet,
    employeeBirthDate,
    override.include_insurance_codes
  );

  const deductions = calculateMandatorySwissDeductions(
    grossSalary,
    employeeBirthDate,
    override.include_insurance_codes
  );

  const totalDeductions = deductions.reduce((sum, d) => sum + d.deduction_amount, 0);
  const netSalary = grossSalary - totalDeductions;

  return {
    gross_salary: grossSalary,
    net_salary: round2(netSalary),
    total_deductions: round2(totalDeductions),
    deductions,
    is_override: true,
    payslip_minimal: override.payslip_minimal
  };
}

export async function createPayrollOverride(
  employeeId: string,
  targetNetAmount: number,
  includeInsuranceCodes: string[] = ['AHV', 'ALV', 'BVG', 'UVG', 'KTG'],
  effectiveFrom: string = new Date().toISOString().split('T')[0],
  notes?: string
): Promise<PayrollOverride> {
  // For now, just return a mock object since we're using hardcoded overrides
  return {
    id: 'override-' + Math.random().toString(36).substr(2, 9),
    employee_id: employeeId,
    effective_from: effectiveFrom,
    target_net_amount: targetNetAmount,
    include_insurance_codes: includeInsuranceCodes,
    exclude_tax: true,
    payslip_minimal: true,
    active: true,
    notes
  };
}